/**
 * Weekly Google Ads management API client (P2.8a).
 *
 * Backend routes:
 *   GET  /google-ads/weekly/runs?tenant_id=&limit=
 *   GET  /google-ads/weekly/runs/{run_id}
 *   POST /google-ads/weekly/trigger?tenant_id=&mode=
 *   POST /google-ads/weekly/approvals/{request_id}/apply
 *   GET  /google-ads/weekly/tenant-mode?tenant_id=
 *   PATCH /google-ads/weekly/tenant-mode?tenant_id=  body {mode}
 *
 * Plus the existing approvals listing for the Approval Queue panel:
 *   GET  /approvals?tenant_id=&user_role=&flow=
 *   GET  /approvals/{request_id}/status
 */
import {
  ADS_MANAGEMENT_READ_TIMEOUT_MS,
  ADS_MANAGEMENT_WRITE_TIMEOUT_MS,
  apiClient,
} from './apiClient';

const WEEKLY_TRIGGER_TIMEOUT_MS = 180_000;

// ---------------------------------------------------------------------------
// Weekly run types
// ---------------------------------------------------------------------------

export type WeeklyMode = 'warn' | 'soft_auto' | 'hard_auto';
export type WeeklyRunStatus = 'running' | 'success' | 'partial' | 'failed';

/**
 * Per-tenant weekly sweep mode. 'off' means the metadata key is absent and
 * the Monday cron skips the tenant entirely.
 */
export type TenantWeeklyMode = 'off' | WeeklyMode;

export interface TenantWeeklyModeState {
  tenant_id: string;
  weekly_mode: TenantWeeklyMode;
  /**
   * tenant_integrations.status — the cron also requires 'connected', so the
   * UI warns when a mode is set on a pending/error/revoked integration.
   */
  integration_status: string | null;
}

export interface WeeklyRunSummary {
  id: string;
  tenant_id: string;
  mode: WeeklyMode;
  status: WeeklyRunStatus;
  run_started_at: string | null;
  run_completed_at: string | null;
  search_terms_flagged: number;
  ads_paused: number;
  keywords_paused: number;
  approval_items_created: number;
  error_count: number;
}

export interface WeeklyRunDetail extends WeeklyRunSummary {
  errors_json: Record<string, string>;
  findings_json: Record<string, unknown>;
  llm_cost_usd: number;
}

/** One tenant's latest-run snapshot on the fleet board. */
export interface FleetRunSummary {
  id: string;
  status: WeeklyRunStatus;
  mode: WeeklyMode;
  run_completed_at: string | null;
  search_terms_flagged: number;
  ads_paused: number;
  keywords_paused: number;
  approval_items_created: number;
  error_count: number;
  /**
   * Per-section monitor-step success. Run `status` reflects only the action
   * engines, so a green run can still have a FAILED monitor step — this
   * exposes it. Keys absent for legacy runs that predate the monitors.
   */
  review_steps: Record<string, boolean>;
  wasted_spend_usd: number;
  projected_underspend_usd: number;
  /** true when the run was partial / evidence missing → $ is a floor, not exact. */
  dollars_incomplete: boolean;
}

/** One row on the cross-client weekly fleet triage board (GET /weekly/fleet). */
export interface FleetTenant {
  tenant_id: string;
  enrollment_mode: WeeklyMode;
  pending_approvals: number;
  latest_run: FleetRunSummary | null;
}

export interface TriggerResult {
  tenant_id: string;
  mode: WeeklyMode;
  run_id: string | null;
  status: WeeklyRunStatus | 'skipped';
  search_terms_flagged: number;
  ads_paused: number;
  keywords_paused: number;
  approval_items_created: number;
}

export interface ApplyApprovalResult {
  request_id: string;
  flow: string;
  run_id: string | null;
  applied: number;
  skipped: number;
  errors_recorded: number;
  note: string | null;
}

export interface SendSlackApprovalResult {
  ok: boolean;
  id: string;
  status: string;
  results: Record<string, string>;
}

export interface SearchTermMetrics {
  search_term: string;
  campaign_name: string | null;
  ad_group_name: string | null;
  /** Explainability: the keyword that triggered this search term (Art M9). */
  triggering_keyword: string | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  cpc_micros: number | null;
  cpa_micros: number | null;
}

export interface SearchTermTriggeredIn {
  campaign_id: string;
  campaign_name: string | null;
  campaign_status: string | null;
  ad_group_id: string | null;
  ad_group_name: string | null;
}

export interface SearchTermNegativeRecommendation {
  id: string;
  search_term: string;
  negative_keyword: string;
  match_type: 'PHRASE' | 'EXACT' | 'BROAD' | string;
  category: string;
  group_key: string;
  reason: string;
  evidence: string;
  metrics: SearchTermMetrics;
  /**
   * Durable applied-state: true when this negative already exists for the
   * tenant — applied by Core (audit-log backed) OR found live in the
   * account's campaign/ad-group/shared-set negatives (ST-9), including an
   * existing broader match type covering this one. Seeds the session
   * appliedKeys set so badges + hiding survive hard page reloads. Optional —
   * older backends omit it.
   */
  already_applied?: boolean;
  /**
   * Why this row is back although a matching negative exists somewhere, in
   * one plain sentence ("Blocked in the A campaign; still showing in the B
   * campaign."). null/absent when there is nothing to explain. Older
   * backends omit it.
   */
  coverage_note?: string | null;
  /**
   * The one word of a longer own-brand name the negative carries ("edelman"
   * of Edelman-Lyon). Only the full brand is vetoed; a part is returned for
   * the operator to review ("Brand collision, review"). null/absent = none;
   * older backends omit it.
   */
  brand_collision?: string | null;
  /** Every (campaign, ad group) the search term triggered in. Older backends omit it. */
  triggered_in?: SearchTermTriggeredIn[];
  /**
   * SERP validation of competitor/brand flags ("brand pattern +
   * SERP-validated"). 'confirmed' = a non-client domain matching the brand
   * dominates the results; 'not_confirmed' = the lookup ran and none does;
   * 'unavailable' = it ran but could not answer. null/absent = validation
   * never ran (feature flag off, or not a competitor flag) — deliberately
   * distinct from 'unavailable' so "off" never reads as "broken".
   */
  serp_validation?: 'confirmed' | 'not_confirmed' | 'unavailable' | null;
  /**
   * True only when the classifier had a real home area (tenant locations
   * or identity text with a geo). Easy Yes on geo_mismatch requires this.
   * Absent on older payloads — treat as unknown, not Easy.
   */
  home_area_known?: boolean;
  /** campaign_exclude | out_of_target | service_area. Absent on older payloads. */
  geo_basis?: string | null;
  /**
   * True only for a clear out-of-footprint geo (foreign state that is not
   * also a town name, or a two-token city like Colorado Springs).
   */
  out_of_footprint?: boolean;
  /**
   * Durable rejected-state: true when the operator previously clicked "No"
   * on this recommendation for the tenant (rejected_ids on the stored review
   * snapshot). The sidebar badge (search_terms_easy) already excludes these
   * — the page hides them too by default so the two counts agree. Optional;
   * older backends omit it.
   */
  rejected?: boolean;
  /**
   * The search term's conversions over EVERY campaign and ad group in the
   * window, not only the row in `metrics` (review of #3045). A term that
   * converted in another campaign is converting here too: flagged, never
   * picked by Select all, never Easy. Absent on older payloads; read it
   * through isConvertingRec / recConversions (convertingSearchTerm.ts).
   */
  term_conversions?: number;
  /** True when any row of this search term converted (a fraction counts). */
  term_converting?: boolean;
}

export interface SearchTermsReviewResult {
  tenant_id: string;
  customer_id: string;
  start_date: string;
  end_date: string;
  search_terms_reviewed: number;
  recommendations: SearchTermNegativeRecommendation[];
  client_safe_summary: string;
  /** Only set when search_terms_reviewed is 0. Distinguishes a confirmed
   *  clean account (verified zero impressions) from an unverified blank —
   *  "0 reviewed" alone cannot tell an operator which one they are looking
   *  at. Absent (undefined/null) means the account-activity probe itself
   *  failed, not that the account is clean. */
  zero_reason?: string | null;
  /** True only when the account had real impressions in this window but the
   *  search-term pull still came back empty — a combination that cannot
   *  happen legitimately. */
  zero_looks_like_a_bug?: boolean;
}

export interface ApplySearchTermNegativeItem {
  recommendation_id?: string | null;
  negative_keyword: string;
  match_type: string;
  category?: string | null;
}

export type NegativeApplyStatus = 'running' | 'success' | 'partial' | 'failed' | 'abandoned';

export interface NegativeApplyKeyword {
  keyword: string;
  match_type?: string;
  set?: string;
  reason?: string;
  errorCode?: string | null;
  message?: string | null;
}

export interface ApplySearchTermNegativesResult {
  /** Always null now: applies no longer create weekly run rows. */
  run_id: string | null;
  /** The apply-ledger row. Absent on the reject-only path. */
  apply_id?: string | null;
  status?: NegativeApplyStatus;
  /** True when this is the stored result of an earlier request with the same key. */
  replayed?: boolean;
  /** Negatives CREATED by this apply. */
  applied: number;
  skipped: number;
  /** Campaign links CREATED by this apply. */
  attached_campaigns: number;
  rejected_logged: number;
  errors_recorded: number;
  client_safe_summary: string;
  /** True when the blast-radius breaker required (and got) typed confirmation. */
  blast_radius_required: boolean;
  conflicts_stripped?: string[];
  conflict_check_ran?: boolean;
  /** `kw_lower|MATCH` now in Google Ads (created or already there). Mark only these. */
  applied_keys?: string[];
  already_present?: number;
  already_attached?: number;
  campaigns_not_supported?: number;
  invalid_keywords?: NegativeApplyKeyword[];
  failed_keywords?: NegativeApplyKeyword[];
  dedupe_verified?: boolean;
  duration_ms?: number;
}

/** One apply-ledger row, as polled. A stale running row reads as abandoned. */
export interface NegativeApplyRecord {
  apply_id: string;
  idempotency_key: string;
  status: NegativeApplyStatus;
  actor: string;
  started_at: string;
  heartbeat_at: string;
  completed_at?: string | null;
  progress: { stage?: string } & Record<string, unknown>;
  result?: ApplySearchTermNegativesResult | null;
  error?: string | null;
}

/** HTTP 409 detail when an apply for this client is already running. */
export interface ApplyInProgressDetail {
  code: 'apply_in_progress';
  apply_id: string;
  idempotency_key: string;
  started_at: string;
  heartbeat_at: string;
  progress: { stage?: string } & Record<string, unknown>;
  actor: string;
  /** True when the running apply is this same request (same key). */
  same_request: boolean;
}

/** Blast-radius breaker assessment returned inside the HTTP 409 detail. */
export interface BlastRadiusAssessment {
  requires_confirmation: boolean;
  reasons: string[];
  summary: Record<string, unknown>;
}

/**
 * HTTP 409 ``detail`` payload emitted by the apply route when the
 * blast-radius breaker demands typed confirmation. The client must resend
 * the same apply request with ``confirmation_text`` set to
 * ``confirmation_phrase`` exactly.
 */
export interface BlastRadiusConfirmationDetail {
  code: 'blast_radius_confirmation_required';
  assessment: BlastRadiusAssessment;
  confirmation_phrase: string;
}

// Approval request shape (matches backend list_approvals_handler output)
export interface WeeklyApprovalRow {
  id: string;
  tenant_id: string;
  flow: string;
  flow_ref_id: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired';
  requested_by: string | null;
  requested_at: string | null;
  expires_at: string | null;
  payload: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Weekly run + trigger
// ---------------------------------------------------------------------------

export async function listRuns(
  tenantId: string,
  limit = 20,
): Promise<WeeklyRunSummary[]> {
  const { data } = await apiClient.get<WeeklyRunSummary[]>(
    '/google-ads/weekly/runs',
    { params: { tenant_id: tenantId, limit } },
  );
  return data;
}

export async function getRun(runId: string): Promise<WeeklyRunDetail> {
  const { data } = await apiClient.get<WeeklyRunDetail>(
    `/google-ads/weekly/runs/${runId}`,
  );
  return data;
}

/**
 * Cross-client weekly triage board — one row per ENROLLED tenant with its
 * latest run's health, counts, monitor-step status, $-at-stake, and pending
 * approvals. Operator-only. 503 when the master flag is off.
 */
export async function listFleet(): Promise<FleetTenant[]> {
  const { data } = await apiClient.get<FleetTenant[]>('/google-ads/weekly/fleet');
  return data;
}

/**
 * Best-effort fetch of the newest weekly optimization run's DETAIL for a
 * tenant. Lists recent runs, picks the newest by ``run_started_at``, and
 * fetches its detail (which carries the system's pause candidates inside
 * ``findings_json``). Returns null when the tenant has no runs yet.
 *
 * Callers surface the system's pause SUGGESTIONS next to the manual pause
 * control — so this is deliberately non-fatal: any error should be treated by
 * the caller as "no suggestions", never as a page break.
 */
export async function getLatestWeeklyRunDetail(
  tenantId: string,
): Promise<WeeklyRunDetail | null> {
  const runs = await listRuns(tenantId, 5);
  if (!runs.length) return null;
  const newest = [...runs].sort((a, b) => {
    const ta = a.run_started_at ? Date.parse(a.run_started_at) : 0;
    const tb = b.run_started_at ? Date.parse(b.run_started_at) : 0;
    return tb - ta;
  })[0];
  return getRun(newest.id);
}

export async function triggerRun(
  tenantId: string,
  mode: WeeklyMode,
): Promise<TriggerResult> {
  const { data } = await apiClient.post<TriggerResult>(
    '/google-ads/weekly/trigger',
    null,
    {
      params: { tenant_id: tenantId, mode },
      timeout: WEEKLY_TRIGGER_TIMEOUT_MS,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Per-tenant weekly sweep mode (GET/PATCH /google-ads/weekly/tenant-mode)
// ---------------------------------------------------------------------------

export async function getTenantWeeklyMode(
  tenantId: string,
): Promise<TenantWeeklyModeState> {
  const { data } = await apiClient.get<TenantWeeklyModeState>(
    '/google-ads/weekly/tenant-mode',
    { params: { tenant_id: tenantId } },
  );
  return data;
}

export async function setTenantWeeklyMode(
  tenantId: string,
  mode: TenantWeeklyMode,
): Promise<TenantWeeklyModeState> {
  const { data } = await apiClient.patch<TenantWeeklyModeState>(
    '/google-ads/weekly/tenant-mode',
    { mode },
    { params: { tenant_id: tenantId } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Operator KPI targets — the "below-plan" goals the weekly monitor reads
// (GET/PUT /google-ads/weekly/targets). Operator-only.
// ---------------------------------------------------------------------------

/** One supported KPI goal row. `target_value` null = no goal set for this metric. */
export interface KpiTargetItem {
  metric: string;
  label: string;
  fmt: 'money' | 'rate';
  lower_better: boolean;
  target_value: number | null;
  tolerance_pct: number;
  effective_from: string | null;
  note: string | null;
  set_by: string | null;
}

export interface KpiTargetsState {
  tenant_id: string;
  targets: KpiTargetItem[];
}

/** One `kpi_plan` finding surfaced in a weekly run (below-plan or beating-plan). */
export interface KpiPlanFinding {
  metric: string;
  label: string;
  kind: 'below_plan' | 'above_plan';
  severity: 'high' | 'medium' | 'info';
  current: number | null;
  target: number;
  tolerance_pct: number;
  miss_pct: number;
  reason: string;
  operator_action_required: boolean;
  auto_action_taken: boolean;
}

export async function getKpiTargets(
  tenantId: string,
): Promise<KpiTargetsState> {
  const { data } = await apiClient.get<KpiTargetsState>(
    '/google-ads/weekly/targets',
    { params: { tenant_id: tenantId } },
  );
  return data;
}

/**
 * Set (target_value > 0) or clear (null / <= 0) one metric's goal, returning
 * the full refreshed target set. Drives FLAGS only — never a Google Ads write.
 */
export async function saveKpiTarget(
  tenantId: string,
  body: {
    metric: string;
    target_value: number | null;
    tolerance_pct?: number;
    note?: string | null;
    set_by?: string | null;
  },
): Promise<KpiTargetsState> {
  const { data } = await apiClient.put<KpiTargetsState>(
    '/google-ads/weekly/targets',
    body,
    { params: { tenant_id: tenantId } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Approvals (weekly subset)
// ---------------------------------------------------------------------------

export const WEEKLY_FLOWS = [
  'p2_negative_keywords',
  'p2_ad_pauses',
  'p2_keyword_pauses',
] as const;

export type WeeklyFlow = (typeof WEEKLY_FLOWS)[number];

export async function listWeeklyApprovals(
  tenantId: string,
  userRole = 'admin',
): Promise<WeeklyApprovalRow[]> {
  // The /approvals list endpoint takes one optional ``flow`` filter so we
  // call it three times in parallel and concat. Cleaner than writing a
  // custom `flow_in` route just for the dashboard.
  const responses = await Promise.all(
    WEEKLY_FLOWS.map((flow) =>
      apiClient.get<WeeklyApprovalRow[]>('/approvals', {
        params: { tenant_id: tenantId, user_role: userRole, flow },
      }),
    ),
  );
  const all = responses.flatMap((r) => r.data);
  // Sort newest first
  all.sort((a, b) => {
    const ta = a.requested_at ? Date.parse(a.requested_at) : 0;
    const tb = b.requested_at ? Date.parse(b.requested_at) : 0;
    return tb - ta;
  });
  return all;
}

export async function resolveApproval(params: {
  requestId: string;
  action: 'approve' | 'reject' | 'cancel';
  tenantId: string;
  resolvedBy: string;
  userRole?: string;
  note?: string;
}): Promise<{
  ok: boolean;
  id: string | null;
  status: string | null;
  action: string;
}> {
  const { data } = await apiClient.post(
    `/approvals/${params.requestId}/resolve`,
    null,
    {
      params: {
        action: params.action,
        tenant_id: params.tenantId,
        user_role: params.userRole ?? 'admin',
        resolved_by: params.resolvedBy,
        note: params.note,
      },
    },
  );
  return data;
}

export async function sendApprovalToSlack(
  requestId: string,
  tenantId: string,
  userRole = 'admin',
): Promise<SendSlackApprovalResult> {
  const { data } = await apiClient.post<SendSlackApprovalResult>(
    `/approvals/${requestId}/send-slack`,
    null,
    {
      params: {
        tenant_id: tenantId,
        user_role: userRole,
      },
    },
  );
  return data;
}

export async function applyApprovedRequest(
  requestId: string,
): Promise<ApplyApprovalResult> {
  const { data } = await apiClient.post<ApplyApprovalResult>(
    `/google-ads/weekly/approvals/${requestId}/apply`,
  );
  return data;
}

export async function reviewSearchTermsNegatives(params: {
  tenantId: string;
  startDate?: string | null;
  endDate?: string | null;
  limit?: number;
}): Promise<SearchTermsReviewResult> {
  const { data } = await apiClient.get<SearchTermsReviewResult>(
    '/google-ads/weekly/search-terms/review',
    {
      params: {
        tenant_id: params.tenantId,
        start_date: params.startDate || undefined,
        end_date: params.endDate || undefined,
        // No default limit: omitting it asks the backend for ALL search
        // terms in the window (Art's PULL step). This `?? 500` silently
        // undid the backend cap raise (#916) for every UI user — the
        // low-cost tail a limit drops is where the junk worth excluding
        // lives.
        limit: params.limit,
      },
      // Unbounded pulls on a large account can exceed the old 45s.
      timeout: ADS_MANAGEMENT_READ_TIMEOUT_MS,
    },
  );
  return data;
}

export async function applySearchTermNegatives(params: {
  tenantId: string;
  selected: ApplySearchTermNegativeItem[];
  rejectedRecommendationIds?: string[];
  confirmed: boolean;
  /**
   * Typed blast-radius confirmation. Omitted on the first attempt; required
   * verbatim (``confirmation_phrase`` from the 409 detail) when the breaker
   * responds with ``blast_radius_confirmation_required``.
   */
  confirmationText?: string;
  /**
   * One key per confirm dialog, reused for the blast-radius resend and every
   * retry. The server never starts a second apply for the same key.
   */
  idempotencyKey?: string;
}): Promise<ApplySearchTermNegativesResult> {
  const { data } = await apiClient.post<ApplySearchTermNegativesResult>(
    '/google-ads/weekly/search-terms/negatives/apply',
    {
      tenant_id: params.tenantId,
      selected: params.selected,
      rejected_recommendation_ids: params.rejectedRecommendationIds ?? [],
      confirmed: params.confirmed,
      // undefined keys are dropped by JSON serialization, so the field is
      // absent on the wire until the user actually typed something.
      confirmation_text: params.confirmationText || undefined,
      idempotency_key: params.idempotencyKey,
    },
    { timeout: ADS_MANAGEMENT_WRITE_TIMEOUT_MS },
  );
  return data;
}

/** One apply by its key (polled while an apply's outcome is unknown). */
export async function getSearchTermNegativeApply(
  tenantId: string,
  idempotencyKey: string,
): Promise<NegativeApplyRecord> {
  const { data } = await apiClient.get<NegativeApplyRecord>(
    `/google-ads/weekly/search-terms/negatives/applies/${idempotencyKey}`,
    { params: { tenant_id: tenantId } },
  );
  return data;
}

/** Recent applies, newest first; `active` = only one still running. */
export async function listSearchTermNegativeApplies(
  tenantId: string,
  options: { active?: boolean } = {},
): Promise<NegativeApplyRecord[]> {
  const { data } = await apiClient.get<NegativeApplyRecord[]>(
    '/google-ads/weekly/search-terms/negatives/applies',
    { params: { tenant_id: tenantId, ...(options.active ? { active: true } : {}) } },
  );
  return data;
}

type HttpErrorShape = { code?: string; response?: { status?: number; data?: unknown } } | null;

/** The app's own errors carry `detail`: a string, or an object with a `code`. */
function hasAppDetail(data: unknown): boolean {
  const detail = (data as { detail?: unknown } | null | undefined)?.detail;
  if (typeof detail === 'string') return detail.trim().length > 0;
  return typeof (detail as { code?: unknown } | null | undefined)?.code === 'string';
}

/**
 * True when we cannot tell whether the apply happened, so it may still be
 * running: no response at all (client timeout, network drop), or a
 * 502/503/504 with no app `detail` (the platform edge, not our app).
 */
export function isOutcomeUnknown(err: unknown): boolean {
  const response = (err as HttpErrorShape)?.response;
  if (!response) return true;
  return [502, 503, 504].includes(response.status ?? 0) && !hasAppDetail(response.data);
}

/** A 5xx our app sent (it has a `detail`): the request is over. */
export function isAppServerError(err: unknown): boolean {
  const response = (err as HttpErrorShape)?.response;
  return (response?.status ?? 0) >= 500 && hasAppDetail(response?.data);
}

/** The 409 `apply_in_progress` detail, or null for any other error. */
export function parseApplyInProgress(err: unknown): ApplyInProgressDetail | null {
  const response = (err as HttpErrorShape)?.response;
  if (response?.status !== 409) return null;
  const detail = (response.data as { detail?: unknown } | undefined)?.detail as
    | Partial<ApplyInProgressDetail>
    | undefined;
  if (!detail || detail.code !== 'apply_in_progress' || typeof detail.idempotency_key !== 'string') {
    return null;
  }
  return detail as ApplyInProgressDetail;
}
